import { type CSSProperties } from "react";

/**
 * PunttThinking — looping "agent is working" indicator.
 *
 * Three dots in a row; one continuously arcs over the pair, stretching into a
 * streak along the curve and collapsing back into a dot on landing. Each dot is
 * the same SVG path rendered as an animated dash, so the streak follows the arc
 * exactly. Geometry is fixed in the viewBox and scales uniformly with `size`.
 *
 * Design source: Puntt thinking animation concepts → leapfrog (4a).
 * Cycle: 3.15s linear, infinite. Three dots at 0 / -1.05s / -2.1s.
 */

export type PunttThinkingProps = {
  /** Rendered height in px. Width is derived (1.837 × height). Default 20 (inline with 14–16px text). */
  size?: number;
  /** `accent` = Puntt blue on light surfaces. `inverse` = white on blue/dark surfaces. */
  tone?: "accent" | "inverse";
  /** Overrides `tone` if you need an arbitrary stroke color. */
  color?: string;
  /** Accessible label announced to screen readers. Pass null to hide from AT. */
  label?: string | null;
  className?: string;
  style?: CSSProperties;
};

/** viewBox is 181 × 98.5 user units → 1.837:1. Never scale the axes independently. */
const ASPECT = 181 / 98.5;
const VIEW_BOX = "-8 27.5 181 98.5";
const ARC = "M0,118 A82.5,82.5 0 0 1 165,118 L0,118";
const DURATION = 3.15;

/** Accent uses the design-token RGB channels so theme swaps stay in sync. */
const TONES = {
  accent: "rgb(var(--puntt-blue-9))",
  inverse: "#FFFFFF",
} as const;

const KEYFRAMES = `
@keyframes punttThinkingOffset {
  0%      { stroke-dashoffset: -611.02px; animation-timing-function: linear; }
  84.44%  { stroke-dashoffset: -1000px; }
  84.45%  { stroke-dashoffset: 0; }
  92.22%  { stroke-dashoffset: 0; }
  93.31%  { stroke-dashoffset: -336.1px; }
  94.40%  { stroke-dashoffset: -519.4px; }
  95.64%  { stroke-dashoffset: -574.4px; }
  97.20%  { stroke-dashoffset: -604.9px; }
  98.76%  { stroke-dashoffset: -608.3px; }
  100%    { stroke-dashoffset: -611px; }
}
@keyframes punttThinkingLength {
  0%, 84.44% { stroke-dasharray: 0.8px 1000px; }
  86%        { stroke-dasharray: 85.5px 1000px; }
  87.55%     { stroke-dasharray: 158.9px 1000px; }
  88.95%     { stroke-dasharray: 207.7px 1000px; }
  90.66%     { stroke-dasharray: 342.2px 1000px; }
  92.22%     { stroke-dasharray: 427.7px 1000px; }
  93.31%     { stroke-dasharray: 165.0px 1000px; }
  94.40%     { stroke-dasharray: 48.9px 1000px; }
  95.64%     { stroke-dasharray: 24.4px 1000px; }
  97.20%     { stroke-dasharray: 6.1px 1000px; }
  98.76%     { stroke-dasharray: 2.7px 1000px; }
  100%       { stroke-dasharray: 0.8px 1000px; }
}
@media (prefers-reduced-motion: reduce) {
  [data-puntt-thinking] path { animation: none !important; }
}
`;

export function PunttThinking({
  size = 20,
  tone = "accent",
  color,
  label = "Thinking",
  className,
  style,
}: PunttThinkingProps) {
  const stroke = color ?? TONES[tone];
  const width = size * ASPECT;

  return (
    <span
      data-testid="puntt-thinking"
      data-puntt-thinking
      className={className}
      role={label ? "status" : undefined}
      aria-live={label ? "polite" : undefined}
      aria-label={label ?? undefined}
      aria-hidden={label ? undefined : true}
      style={{
        display: "inline-flex",
        width,
        height: size,
        flex: "none",
        ...style,
      }}
    >
      <style>{KEYFRAMES}</style>
      <svg
        viewBox={VIEW_BOX}
        width={width}
        height={size}
        fill="none"
        style={{ display: "block" }}
        aria-hidden
      >
        {[0, -DURATION / 3, -(DURATION / 3) * 2].map((delay) => (
          <path
            key={delay}
            d={ARC}
            pathLength={1000}
            stroke={stroke}
            strokeWidth={16}
            strokeLinecap="round"
            style={{
              animation:
                `punttThinkingOffset ${DURATION}s linear infinite ${delay}s, ` +
                `punttThinkingLength ${DURATION}s linear infinite ${delay}s`,
            }}
          />
        ))}
      </svg>
    </span>
  );
}

export default PunttThinking;
